import { useClientPortalAPI, useSubmit } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { Grid, showToast } from "@certn/cascadia-react";
import { Formik } from "formik";
import { Key } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { object, string } from "yup";

import { applyRequestorFields, useRequestorRoleMeta } from "../requestor";
import { useCreateUserContext } from "./CreateUserContext";
import { CreateUserPageLayout } from "./CreateUserPageLayout";
import { useUser } from "../../../../hooks/useUser";
import { AssignedGroupsList } from "../AssignedGroupsList";
import { UserGroupsSelect } from "../UserGroupsSelect";

export function CreateUserGroupsStep() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { details: userDetails } = useUser();
  const { user, setUser } = useCreateUserContext();
  const schema = object({
    full_name: string().required().label(t("Full name")),
    email: string().email().required().label(t("Email address")),
    role: string().required().label(t("Role")),
  });
  const { data: groups = [] } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/customer-groups-for-user-management/`
  );
  const { data: roles } = useClientPortalAPI(
    "/api/internal/client-portal/roles/"
  );
  const { requestorRoleId } = useRequestorRoleMeta({
    roles,
    enableRequestorRole: false,
  });
  const { submit } = useSubmit(`/api/internal/client-portal/users/create/`, {
    throwErrors: true,
    showToastOnError: true,
    onSuccess: () => {
      showToast(
        t("Success. {{name}} has been added as a user.", {
          name: user.full_name,
        }),
        { type: "success" }
      );
      navigate(`/settings/users`);
    },
  });
  const onAdd = (selectedGroups: Key[]) => {
    setUser({
      ...user,
      customer_groups: [...user.customer_groups, ...selectedGroups] as string[],
    });
  };

  return (
    <Formik
      initialValues={user}
      onSubmit={(values) => {
        const payload = applyRequestorFields(
          {
            ...values,
            customer_groups: user.customer_groups,
            customer_account: userDetails?.client_account?.id || "",
          },
          values.role,
          requestorRoleId
        );
        submit(payload);
      }}
      validationSchema={schema}
    >
      {({ handleSubmit, isSubmitting }) => {
        return (
          <CreateUserPageLayout
            disableCompletion={user.customer_groups.length === 0}
            onComplete={handleSubmit}
            submitting={isSubmitting}
          >
            <Grid form>
              <UserGroupsSelect
                onDone={onAdd}
                userGroups={[...user.customer_groups]}
              />
              <AssignedGroupsList
                userGroups={
                  [
                    ...user.customer_groups.map((groupId) =>
                      groups.find((group) => group.id === groupId)
                    ),
                  ] as unknown as components["schemas"]["UserCustomerGroupsListCustomerGroups"][]
                }
                onRemove={(groupToRemove) =>
                  setUser({
                    ...user,
                    customer_groups: user.customer_groups.filter(
                      (group) => group !== groupToRemove.id
                    ),
                  })
                }
              />
            </Grid>
          </CreateUserPageLayout>
        );
      }}
    </Formik>
  );
}
